import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, Anchor, Users, MapPin, Star, Clock, Fish, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hero images - Your Calvi photo + turquoise water
const HERO_IMAGES = [
  "https://customer-assets.emergentagent.com/job_calvi-fishing-book/artifacts/0ysoa4g0_Capture%20d%E2%80%99e%CC%81cran%202026-03-23%20a%CC%80%2000.29.28.png",
  "https://customer-assets.emergentagent.com/job_8c77b53a-833d-4082-88a3-825ccd2bdd54/artifacts/92sjj6sn_PECHE%20EN%20MER.avif"
];

const HomePage = () => {
  const { t, getLocalizedField } = useLanguage();
  const [trips, setTrips] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);

  // Auto-rotate hero images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first if needed
        await axios.post(`${API}/seed`).catch(() => {});
        
        const [tripsRes, galleryRes] = await Promise.all([
          axios.get(`${API}/trips`),
          axios.get(`${API}/gallery`)
        ]);
        setTrips(tripsRes.data.slice(0, 4));
        setGallery(galleryRes.data.slice(0, 4));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const features = [
    { icon: Users, text: t('features.guide') },
    { icon: Anchor, text: t('features.boat') },
    { icon: MapPin, text: t('features.region') },
    { icon: Star, text: t('features.privatization') },
    { icon: Users, text: t('features.children') },
    { icon: Fish, text: t('features.authentic') },
  ];

  const nextHero = () => setCurrentHeroIndex((prev) => (prev + 1) % HERO_IMAGES.length);
  const prevHero = () => setCurrentHeroIndex((prev) => (prev - 1 + HERO_IMAGES.length) % HERO_IMAGES.length);

  return (
    <div>
      {/* Hero Section with Carousel */}
      <section className="hero-section relative" data-testid="hero-section">
        {HERO_IMAGES.map((img, index) => (
          <img
            key={index}
            src={img}
            alt="Calvi Fishing"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              index === currentHeroIndex ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-[#000000]/85 via-[#000000]/50 to-transparent" />
        
        {/* Carousel Controls */}
        <button 
          onClick={prevHero}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm items-center justify-center text-white hover:bg-white/30 transition-colors z-20 hidden md:flex"
        >
          <CaretLeft size={24} />
        </button>
        <button 
          onClick={nextHero}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm items-center justify-center text-white hover:bg-white/30 transition-colors z-20 hidden md:flex"
        >
          <CaretRight size={24} />
        </button>
        
        {/* Dots indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {HERO_IMAGES.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentHeroIndex(index)}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === currentHeroIndex ? 'bg-[#b7ede0]' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
        
        <div className="hero-content max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="animate-fade-up">
            <span className="inline-block px-4 py-2 bg-[#b7ede0]/30 text-[#b7ede0] rounded-full text-sm font-medium mb-6">
              Calvi, Balagne - Corse
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Playfair_Display'] tracking-tight">
              {t('hero.title')}<br /><span className="text-[#b7ede0]">{t('hero.titleLine2')}</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-lg">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/trips">
                <Button 
                  size="lg" 
                  className="bg-[#b7ede0] hover:bg-[#9ee5d4] text-[#000000] rounded-full px-8 gap-2 font-semibold"
                  data-testid="hero-cta-btn"
                >
                  {t('hero.cta')}
                  <ArrowRight size={20} />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full px-8 border-[#b7ede0] text-[#b7ede0] hover:bg-[#b7ede0] hover:text-[#000000]"
                >
                  {t('contact.title')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-[#b7ede0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#000000] font-['Playfair_Display'] mb-4">
              CALVI FISHING ...c'est
            </h2>
            <p className="text-[#000000]/70 max-w-2xl mx-auto">
              Des sorties en mer inoubliables, alliant passion, technique et découverte
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className={`flex flex-col items-center text-center p-6 bg-white rounded-2xl card-shadow hover-lift animate-fade-up stagger-${index + 1}`}
              >
                <div className="w-14 h-14 rounded-full bg-[#000000]/10 flex items-center justify-center mb-4">
                  <feature.icon size={28} className="text-[#000000]" />
                </div>
                <p className="text-sm font-medium text-[#000000]">{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trips Preview */}
      <section className="py-20 bg-white" data-testid="trips-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#000000] font-['Playfair_Display'] mb-2">
                {t('trips.title')}
              </h2>
              <p className="text-[#4B5563]">{t('trips.maxParticipants')}</p>
            </div>
            <Link to="/trips">
              <Button variant="outline" className="rounded-full gap-2 border-[#000000] text-[#000000] hover:bg-[#000000] hover:text-[#b7ede0]" data-testid="view-all-trips-btn">
                Voir toutes les sorties
                <ArrowRight size={18} />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden card-shadow">
                  <div className="skeleton h-48 w-full" />
                  <div className="p-6 space-y-3">
                    <div className="skeleton h-6 w-3/4 rounded" />
                    <div className="skeleton h-4 w-full rounded" />
                    <div className="skeleton h-10 w-1/2 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {trips.map((trip, index) => (
                <Link 
                  key={trip.id} 
                  to={`/trips/${trip.id}`}
                  className={`trip-card animate-fade-up stagger-${index + 1}`}
                  data-testid={`trip-card-${index}`}
                >
                  <div className="relative">
                    <img
                      src={trip.image_url}
                      alt={getLocalizedField(trip, 'name')}
                      className="trip-card-image"
                    />
                    <div className="absolute top-4 right-4 px-3 py-1 bg-[#b7ede0] backdrop-blur-sm rounded-full text-sm font-medium text-[#000000]">
                      <Clock size={14} className="inline mr-1" />
                      {trip.duration_options && trip.duration_options.length > 0
                        ? trip.duration_options.map(o => o.label || `${o.duration_hours}h`).join(' / ')
                        : `${trip.duration_hours}h`}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-[#000000] mb-2 font-['Playfair_Display']">
                      {getLocalizedField(trip, 'name')}
                    </h3>
                    <p className="text-sm text-[#4B5563] line-clamp-2 mb-4">
                      {getLocalizedField(trip, 'description').substring(0, 80)}...
                    </p>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold text-[#000000]">
                          {trip.duration_options && trip.duration_options.length > 0
                            ? `${Math.min(...trip.duration_options.map(o => o.price_per_person))}€`
                            : `${trip.price_per_person}€`}
                        </span>
                        <span className="text-sm text-[#4B5563]"> {t('trips.perPerson')}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Boat Section */}
      <section className="py-20 bg-[#000000]" data-testid="boat-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_calvi-fishing-book/artifacts/p0mtsa4h_IMG_5987%202_HEIC.avif"
                alt="Andilana"
                className="rounded-2xl w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#b7ede0] rounded-2xl p-6 text-[#000000] hidden sm:block">
                <div className="text-4xl font-bold font-['Playfair_Display']">350 CV</div>
                <div className="text-sm opacity-80">Mercury Engine</div>
              </div>
            </div>
            <div className="text-white">
              <span className="inline-block px-4 py-2 bg-[#b7ede0]/20 text-[#b7ede0] rounded-full text-sm font-medium mb-6">
                Bayliner Trophy 2503 CC
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 font-['Playfair_Display']">
                {t('boat.title')}: {t('boat.name')}
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed">
                {t('boat.description')}
              </p>
              <p className="text-gray-400 mb-8 leading-relaxed">
                {t('boat.specs')}
              </p>
              <Link to="/trips">
                <Button className="bg-[#b7ede0] hover:bg-[#9ee5d4] text-[#000000] rounded-full px-8 gap-2 font-semibold">
                  {t('nav.book')}
                  <ArrowRight size={20} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      {gallery.length > 0 && (
        <section className="py-20 bg-[#b7ede0]" data-testid="gallery-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#000000] font-['Playfair_Display'] mb-2">
                  {t('gallery.title')}
                </h2>
                <p className="text-[#000000]/70">{t('gallery.subtitle')}</p>
              </div>
              <Link to="/gallery">
                <Button variant="outline" className="rounded-full gap-2 border-[#000000] text-[#000000] hover:bg-[#000000] hover:text-[#b7ede0]">
                  Voir la galerie
                  <ArrowRight size={18} />
                </Button>
              </Link>
            </div>
            <div className="gallery-grid">
              {gallery.map((image, index) => (
                <div key={image.id} className={`gallery-item animate-fade-up stagger-${index + 1}`}>
                  <img src={image.image_url} alt={getLocalizedField(image, 'caption')} />
                  <div className="gallery-overlay">
                    <p className="text-white text-sm">{getLocalizedField(image, 'caption')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-['Playfair_Display']">
            En route pour l'aventure
          </h2>
          <p className="text-gray-400 mb-8 text-lg">
            4 participants maximum par prestation
          </p>
          <Link to="/trips">
            <Button 
              size="lg" 
              className="bg-[#b7ede0] hover:bg-[#9ee5d4] text-[#000000] rounded-full px-12 py-6 text-lg gap-2 font-semibold"
              data-testid="cta-book-btn"
            >
              {t('nav.book')}
              <ArrowRight size={24} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
